import { create } from 'zustand';

interface DialogState {
  // User dialog
  OneUser: {
    id: string | null;
    isOpen: boolean;
  };

  // Client dialog
  OneClient: {
    id: number | null;
    isOpen: boolean;
  };

  // Offer dialog
  OneOffer: {
    id: string | null;
    isOpen: boolean;
  };

  // Group dialog
  OneGroup: {
    id: string | null;
    isOpen: boolean;
  };

  // Global loading
  globalLoading: {
    isLoading: boolean;
  };

  // Sale invoice dialog
  OneSaleInvoice: {
    id: number | null;
    isOpen: boolean;
  };

  // Request dialog
  OneRequest: {
    id: string | number | null;
    isOpen: boolean;
  };

  // Global search
  isGlobalSearchOpen: boolean;

  // Item dialog
  OneItem: {
    id: number | null;
    isOpen: boolean;
  };

  // Client Balance Statement dialog
  ClientBalanceStatement: {
    clientId: number | null;
    startDate: Date | null;
    endDate: Date | null;
    isOpen: boolean;
  };
}

interface DialogActions {
  // User dialog actions
  openUserDialog: (id: string) => void;
  closeUserDialog: () => void;

  // Client dialog actions
  openClientCardDialog: (id: number) => void;
  closeClientCardDialog: () => void;

  // Offer dialog actions
  openOneOffer: (id: string) => void;
  closeOneOffer: () => void;

  // Group dialog actions
  openGroupDialog: (id: string) => void;
  closeGroupDialog: () => void;

  // Global loading actions
  openGlobalLoading: () => void;
  closeGlobalLoading: () => void;

  // Sale invoice dialog actions
  openSaleInvoice: (id: number) => void;
  closeSaleInvoice: () => void;

  // Request dialog actions
  openRequestDialog: (id: string | number) => void;
  closeRequestDialog: () => void;

  // Global search actions
  openGlobalSearch: () => void;
  closeGlobalSearch: () => void;

  // Item dialog actions
  openItemDialog: (id: number) => void;
  closeItemDialog: () => void;

  // Client Balance Statement dialog actions
  openClientBalanceStatement: (clientId?: number, startDate?: Date | null, endDate?: Date | null) => void;
  closeClientBalanceStatement: () => void;

  // Utility actions
  closeAllDialogs: () => void;
}

export type DialogStore = DialogState & DialogActions;

export const useDialogStore = create<DialogStore>((set) => ({
  // Initial state
  OneUser: {
    id: null,
    isOpen: false,
  },
  OneClient: {
    id: null,
    isOpen: false,
  },
  OneOffer: {
    id: null,
    isOpen: false,
  },
  OneGroup: {
    id: null,
    isOpen: false,
  },
  globalLoading: {
    isLoading: false,
  },
  OneSaleInvoice: {
    id: null,
    isOpen: false,
  },
  OneRequest: {
    id: null,
    isOpen: false,
  },
  isGlobalSearchOpen: false,
  OneItem: {
    id: null,
    isOpen: false,
  },
  ClientBalanceStatement: {
    clientId: null,
    startDate: null,
    endDate: null,
    isOpen: false,
  },
  // User dialog actions
  openUserDialog: (id: string) =>
    set({
      OneUser: {
        id,
        isOpen: true,
      },
    }),

  closeUserDialog: () =>
    set((state) => ({
      OneUser: {
        ...state.OneUser,
        isOpen: false,
      },
    })),

  // Client dialog actions
  openClientCardDialog: (id: number) =>
    set({
      OneClient: {
        id,
        isOpen: true,
      },
    }),

  closeClientCardDialog: () =>
    set({
      OneClient: {
        id: null,
        isOpen: false,
      },
    }),

  // Offer dialog actions
  openOneOffer: (id: string) =>
    set({
      OneOffer: {
        id,
        isOpen: true,
      },
    }),

  closeOneOffer: () =>
    set({
      OneOffer: {
        id: null,
        isOpen: false,
      },
    }),

  // Group dialog actions
  openGroupDialog: (id: string) =>
    set({
      OneGroup: {
        id,
        isOpen: true,
      },
    }),

  closeGroupDialog: () =>
    set({
      OneGroup: {
        id: null,
        isOpen: false,
      },
    }),

  // Global loading actions
  openGlobalLoading: () =>
    set({
      globalLoading: {
        isLoading: true,
      },
    }),

  closeGlobalLoading: () =>
    set({
      globalLoading: {
        isLoading: false,
      },
    }),

  // Sale invoice dialog actions
  openSaleInvoice: (id: number) =>
    set({
      OneSaleInvoice: {
        id,
        isOpen: true,
      },
    }),

  closeSaleInvoice: () =>
    set({
      OneSaleInvoice: {
        id: null,
        isOpen: false,
      },
    }),

  // Request dialog actions
  openRequestDialog: (id: string | number) =>
    set({
      OneRequest: {
        id,
        isOpen: true,
      },
    }),

  closeRequestDialog: () =>
    set({
      OneRequest: {
        id: null,
        isOpen: false,
      },
    }),

  // Global search actions
  openGlobalSearch: () =>
    set({
      isGlobalSearchOpen: true,
    }),

  closeGlobalSearch: () =>
    set({
      isGlobalSearchOpen: false,
    }),

  // Item dialog actions
  openItemDialog: (id: number) =>
    set({
      OneItem: {
        id,
        isOpen: true,
      },
    }),

  closeItemDialog: () =>
    set({
      OneItem: { id: null, isOpen: false },
    }),

  // Client Balance Statement dialog actions
  openClientBalanceStatement: (clientId?: number, startDate?: Date | null, endDate?: Date | null) =>
    set({
      ClientBalanceStatement: {
        clientId: clientId || null,
        startDate: startDate || null,
        endDate: endDate || null,
        isOpen: true,
      },
    }),

  closeClientBalanceStatement: () =>
    set({
      ClientBalanceStatement: {
        clientId: null,
        startDate: null,
        endDate: null,
        isOpen: false,
      },
    }),

  // Utility action to close all dialogs
  closeAllDialogs: () =>
    set({
      OneUser: { id: null, isOpen: false },
      OneClient: { id: null, isOpen: false },
      OneOffer: { id: null, isOpen: false },
      OneGroup: { id: null, isOpen: false },
      OneSaleInvoice: { id: null, isOpen: false },
      OneRequest: { id: null, isOpen: false },
      isGlobalSearchOpen: false,
      ClientBalanceStatement: { clientId: null, startDate: null, endDate: null, isOpen: false },
    }),
}));
